<template>
  <div class="rt-lookup-outer">
    <div
      ref="lookup"
      class="rt-lookup"
      :class="[
        multiple ? 'is-multiple' : 'is-single',
        multiple && max && tagInputFocus ? 'rt-lookup-input-focus' : '',
        max ? '' : 'rt-lookup-no-height'
      ]"
      @click.stop="rtLookupShowHandler"
    >
      <div
        v-if="$slots.prefix"
        ref="prefix"
        class="rt-lookup-prefix"
      >
        <slot name="prefix" />
      </div>

      <div
        :class="[
          'lookup-wrap',
          isLink ? 'is-link' : '',
          tagInputFocus ? 'input-focus' : ''
        ]"
        @mouseenter="inputHovering = true"
        @mouseleave="inputHovering = false"
      >
        <div
          v-if="list && list.length > 0"
          class="lookup-main"
          @click="focusTagInput"
        >
          <span
            ref="tag"
            :style="{
              position: 'absolute',
              opacity: 0,
              'pointer-events': 'none'
            }"
          >
            <rt-tag
              v-for="(item, index) in list"
              :key="index"
              size="medium"
              :disabled="computedDisabled"
              :closable="!computedDisabled"
              class="rt-tag-box"
              type="outline"
              :text-type="isLink ? 'primary' : ''"
              @close="() => {}"
            >{{ item[alias.name] }}</rt-tag>
          </span>
          <template v-if="!!$scopedSlots.popover">
            <template v-if="max && list.length > maxIndex && !tagInputFocus">
              <div class="tag">
                <rt-popover
                  v-for="(item, index) in list.slice(0, maxIndex)"
                  :key="index"
                  v-bind="computedPopoverOptions"
                >
                  <rt-tag
                    slot="reference"
                    :disabled="computedDisabled"
                    :closable="!computedDisabled"
                    type="outline"
                    size="medium"
                    :text-type="isLink ? 'primary' : ''"
                    @click.native.stop="onTagClick(item, item[alias.name])"
                    @close="del(index)"
                  >
                    {{ item[alias.name] }}
                  </rt-tag>

                  <slot
                    :item="item"
                    name="popover"
                  ></slot>
                </rt-popover>
                <rt-tooltip
                  :content="contentPopover"
                  placement="bottom"
                >
                  <rt-tag
                    :disabled="computedDisabled"
                    :closable="!computedDisabled"
                    type="outline"
                    size="medium"
                    :text-type="isLink ? 'primary' : ''"
                    @close="del(list.length - 1)"
                  >
                    +{{ list.length - maxIndex }}
                  </rt-tag>
                </rt-tooltip>
              </div>
            </template>
            <template v-else>
              <rt-popover
                v-for="(item, index) in list"
                :key="index"
                v-bind="computedPopoverOptions"
              >
                <rt-tag
                  slot="reference"
                  :disabled="computedDisabled"
                  :closable="multiple ? !computedDisabled : false"
                  type="outline"
                  size="medium"
                  :text-type="isLink ? 'primary' : ''"
                  :max-width="computedDisabled && list.length === 1 ? '100%': '200px'"
                  @click.native.stop="onTagClick(item, item[alias.name])"
                  @close="del(index)"
                >
                  {{ item[alias.name] }}
                </rt-tag>

                <slot
                  :item="item"
                  name="popover"
                ></slot>
              </rt-popover>
            </template>
          </template>
          <template v-else>
            <template v-if="max && list.length > maxIndex && !tagInputFocus">
              <div class="tag">
                <rt-tag
                  v-for="(item, index) in list.slice(0, maxIndex)"
                  :key="index"
                  :disabled="computedDisabled"
                  :closable="!computedDisabled"
                  type="outline"
                  size="medium"
                  :text-type="isLink ? 'primary' : ''"
                  @click.native.stop="onTagClick(item, item[alias.name])"
                  @close="del(index)"
                >
                  {{ item[alias.name] }}
                </rt-tag>
                <rt-tooltip
                  :content="contentPopover"
                  placement="bottom"
                >
                  <rt-tag
                    :disabled="computedDisabled"
                    :closable="!computedDisabled"
                    type="outline"
                    size="medium"
                    :text-type="isLink ? 'primary' : ''"
                    @close="del(list.length - 1)"
                  >
                    +{{ list.length - maxIndex }}
                  </rt-tag>
                </rt-tooltip>
              </div>
            </template>

            <template v-else>
              <rt-tag
                v-for="(item, index) in list"
                :key="index"
                :disabled="computedDisabled"
                :closable="multiple ? !computedDisabled : false"
                type="outline"
                size="medium"
                :max-width="computedDisabled && list.length === 1 ? '100%': '200px'"
                :text-type="isLink ? 'primary' : ''"
                @click.native.stop="onTagClick(item, item[alias.name])"
                @close="del(index)"
              >
                {{ item[alias.name] }}
              </rt-tag>
            </template>
          </template>
          <rt-input
            v-if="tagInputFocus && multiple && lookupHeight > 35"
            ref="tagInput"
            v-model="tagInputText"
            :class="{ 'rt-lookup-allow-input': allowInput }"
            class="rt-lookup-tag-input"
            type="autocomplete"
            :fetch-suggestions="fetchSuggestions"
            :trigger-on-focus="triggerOnFocus"
            :show-suffix-icon="false"
            :clearable="false"
            :highlight-first-item="computedHighlightFirstItem"
            :readonly="!allowInput"
            :disabled="computedDisabled"
            :hide-option-desc="hideOptionDesc"
            @select="addValue"
            @input="inputHandler"
            @blur="blurHandler"
            @keydown.native="keydownHandler"
          ></rt-input>
        </div>
        <rt-input
          ref="input"
          v-model="inputText"
          :class="{ 'rt-lookup-allow-input': allowInput }"
          type="autocomplete"
          :fetch-suggestions="fetchSuggestions"
          :trigger-on-focus="triggerOnFocus"
          :show-suffix-icon="false"
          :clearable="false"
          :highlight-first-item="computedHighlightFirstItem"
          :readonly="!allowInput"
          :placeholder="computedPlaceholder"
          :disabled="computedDisabled"
          :hide-option-desc="hideOptionDesc"
          @select="addValue"
          @input="inputHandler"
          @change="changeHandler"
          @focus="focusHandler"
          @blur="blurHandler"
          @mouseenter.native="inputHovering = true"
          @mouseleave.native="inputHovering = false"
          @keydown.native="keydownHandler"
        ></rt-input>
      </div>

      <rt-icon
        v-if="showClose"
        class="rt-lookup-suffix rt-lookup-suffix_close rt-lookup-clearicon-position"
        :name="computedIconName"
        :class="{ 'rt-lookup-clearicon-position': allowInput }"
        @click.stop="clear"
        @mouseenter.native="inputHovering = true"
        @mouseleave.native="inputHovering = false"
       style="margin-right: 30px;width:24px;height:24px"
      />

    <rt-tooltip
        :disabled="!iconContent"
        :content="iconContent"
        placement="right"
      >
        <rt-icon
          v-show="!computedDisabled"
          class="rt-lookup-suffix rt-lookup-suffix_lookup lookup-icon"
          :name="suffixIconName"
          
          @click.stop="show"
          @mouseenter.native="inputHovering = false"
          @mouseleave.native="inputHovering = false"
        />
      </rt-tooltip>

      <rt-lookup-view
        ref="lookupView"
        :fetch="fetchRecord"
        :display-fields="displayFields"
        :list="list"
        :multiple="multiple"
        :title="title"
        :size="size"
        :total="total"
        :alias="alias"
        :request-alias="requestAlias"
        :keep-selected="keepSelected"
        :page-fields="innerPageFields"
        v-bind="$attrs"
        @confirm="confirm"
        @change="change"
      >
        <slot
          slot="footer-left"
          name="footer-left"
        >
        </slot>
      </rt-lookup-view>
    </div>
  </div>
</template>

<script>
import RtIcon from '../icon/rtIcon';
import RtLookupView from './rtLookupView';
import RtInput from '../form/rtInput.vue';
import RtTag from '../tag/rtTag.vue';
import RtTooltip from '../tooltip/rtTooltip.vue';
import mixin from './mixin.js';
import xpcConfig from '../../config';
import _ from 'lodash';

export default {
  name: 'RtLookup',
  components: {
    RtIcon,
    RtLookupView,
    RtInput,
    RtTag,
    RtTooltip
  },
  mixins: [mixin],
  computed: {
    computedEmptyPlaceholder () {
      return this.emptyPlaceholder || xpcConfig.get('emptyPlaceholder');
    },
    computedPlaceholder () {
      if (this.list.length > 0) {
        return '';
      }
      if (this.placeholder && !this.computedDisabled) {
        return this.placeholder;
      } else {
        return this.computedDisabled
          ? this.computedEmptyPlaceholder
          : this.$tx('请选择', 'xpc.common.pleaseSelect');
      }
    },
    /**
     * true：高亮搜索结果第一项，不允许保存不存在的数据
     * false：高亮搜索结果，允许保存不存在的数据
     */
    computedHighlightFirstItem () {
      return !this.allowCustomValue;
    },

    showClose () {
      return (
        (!!this.inputText || this.list.length > 0) &&
        this.inputHovering &&
        !this.computedDisabled
      );
    },

    computedIconName () {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    }
  },

  mounted () {
    document.addEventListener('click', this.documentClick);

    this.$nextTick(() => {
      if (!this.inputSizeObserver) {
        this.inputSizeObserver = new ResizeObserver(
          _.throttle(entries => {
            entries.forEach(entry => {
              this.inputWidth = entry.contentRect.width;
              this.lookupHeight = entry.contentRect.height;
            });
          }),
          500
        );
        this.inputSizeObserver.observe(this.$refs.lookup);
      }

      if (
        this._events &&
        this._events['click'] &&
        this._events['click'].length > 0
      ) {
        this.isLink = true;
      }
    });
  },
  beforeDestroy () {
    if (this.inputSizeObserver) this.inputSizeObserver.disconnect();

    document.removeEventListener('click', this.documentClick);
  },
  methods: {
    change (options) {
      this.$emit('change', options.data)
    },
    documentClick () {
      this.tagInputFocus = false;
    },
    focusTagInput () {
      this.focusHandler();
    },
    blurHandler () {
      this.$nextTick(() => {
        this.tagInputText = '';
      });
    },
    focusHandler () {
      this.tagInputFocus = true;
      if (this.inputFocusTimer) clearTimeout(this.inputFocusTimer);
      this.inputFocusTimer = this.$setTimeout(() => {
        this.$refs.tagInput &&
          this.$refs.tagInput.$el &&
          this.$refs.tagInput.$el.querySelector('input').focus();
      }, 200);
    },
    toEmptyList () {
      this.list = [];
    },
    keydownHandler (event) {
      const keyCode = event.keyCode;
      if (
        keyCode === 8 &&
        this.list.length > 0 &&
        !this.inputText &&
        !this.tagInputText
      ) {
        this.list.pop();
        if (this.multiple) {
          this.$emit('input', this.list);
        } else {
          this.$emit('input', this.inputText);
        }
      }
    },
    changeHandler (val) {
      if (this.allowCustomValue && this.$xpc.isString(val)) {
        this.inputText = val;
        this.toEmptyList();
      } else {
        this.$setTimeout(() => {
          this.inputText = null;
          this.$refs.input && this.$refs.input.handleClearQueryString();
        }, 200);
      }
    },
    inputHandler (val) {
      this.tagInputText = val;
      if (this.allowCustomValue) {
        this.$emit('input', val);
      }
    },
    /**
     * @public 向 lookup 输入框中添加值
     */
    addValue (item) {
      const obj = {};
      const { alias, multiple } = this;
      let id = '';
      let name = '';
      if (multiple) {
        let newItems = item;
        if (!this.$xpc.isArray(item) && this.$xpc.isObject(item)) {
          newItems = [newItems];
        }
        // 去除重复数组项
        this.list = new Set([...this.list, ...newItems]);
        this.list = Array.from(this.list);
        this.focusHandler();
        this.$emit('input', this.list);
        this.$emit('change', this.list);
        this.$emit('select', this.list);
      } else {
        id = item ? item[alias.id] : '';
        name = item ? item[alias.name] : '';
        obj[alias.id] = id;
        obj[alias.name] = name;
        this.$emit('input', obj);
        this.$emit('change', obj);
        this.$emit('select', obj);
      }
    },
    // 标签点击事件
    onTagClick (item, text) {
      this.$emit('click', item, text);
    },
    del (index) {
      if (this.computedDisabled) {
        return;
      }
      this.list.splice(index, 1);
      this.$emit('input', this.list);
      this.$emit('confirm', this.list);
    },
    clear () {
      if (this.computedDisabled) {
        return;
      }
      if (this.multiple) {
        this.toEmptyList();
        this.$emit('input', this.list);
        this.$emit('confirm', this.list);
        this.dispatch('ElFormItem', 'el.form.change', this.list);
      } else {
        this.toEmptyList();
        this.$emit('input', null);
        this.$emit('confirm', null);
        this.dispatch('ElFormItem', 'el.form.change', null);
      }
      this.$emit('clear');
    },
    confirm (selected) {
      const obj = {};
      const { multiple, alias } = this;
      let id = '';
      let name = '';
      if (multiple) {
        this.list = this.$refs.lookupView.selectList;
        this.$emit('input', this.list);
        this.$emit('confirm', this.list);
        this.dispatch('ElFormItem', 'el.form.change', this.list);
      } else {
        if (selected) {
          id = selected[alias.id];
          name = selected[alias.name];
          obj[alias.id] = id;
          obj[alias.name] = name;
          this.inputText = name;
          this.$emit('input', obj);
          this.$emit('confirm', obj);
          this.dispatch('ElFormItem', 'el.form.change', obj);
        } else {
          this.toEmptyList();
          this.$emit('input', null);
        }
      }
      this.selected = null;
      this.$refs.lookupView.$refs.table.$refs.table.clearSelection();
    }
  }
};
</script>
<style scoped>
.lookup-icon{
    min-width: 32px;
    padding: 0 !important;
    display: flex;
    /* text-align: center; */
    /* align-items: center; */
    justify-content: center;
}
</style>


// WEBPACK FOOTER //
// rtLookup.vue?9db5d78e